import React, { useEffect, useRef } from "react";
import { useForm, Controller } from "react-hook-form";
import { useNavigate, useParams, Navigate } from "react-router-dom";
import { Editor } from 'primereact/editor';

// Components
import DashboardLayout from "./components/Layout";
import useRole from "./components/useRole";
// Api
import { getEventByIdAdmin, updateEventByIdAdmin, uploadEventPhoto, uploadGalleryPhoto, deleteGalleryPhoto } from "../../api/plan";
import { getPaymentModes } from "../../api/payment-mode";
import { getVenuesAdmin } from "../../api/venue";

export function dateTimeToInputValue(dateString) {
  const date = new Date(dateString);

  // Convert to local time
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);

  return local.toISOString().slice(0, 16); // "YYYY-MM-DDTHH:MM"
}

const SettingsPage = () => {
  const { eventId } = useParams();
  const { isAdmin } = useRole();
  const navigate = useNavigate();
  const [paymentModes, setPaymentModes] = React.useState([]);
  const [currentPhoto, setCurrentPhoto] = React.useState(null);
  const [photoFile, setPhotoFile] = React.useState(null);
  const [isUploadingPhoto, setIsUploadingPhoto] = React.useState(false);
  const [galleryPhotos, setGalleryPhotos] = React.useState([]);
  const [isUploadingGallery, setIsUploadingGallery] = React.useState(false);
  const [venues, setVenues] = React.useState([]);
  // Events saved before seat selection existed have no flag; don't add one unless it's turned on
  const hasSeatSettingRef = useRef(false);

  const {
    register,
    handleSubmit,
    setValue,
    setError,
    control,
    watch,
    formState: { isSubmitting, errors },
  } = useForm();

  const hasSeatSelection = watch("hasSeatSelection");
  const customFieldLabel = watch("customFieldLabel");
  const customFieldOptions = watch("customFieldOptions");

  useEffect(() => {
    getEventByIdAdmin(eventId).then((res) => {
      const data = res?.data?.data;
      if (data) {
        setCurrentPhoto(data.photo || null);
        setGalleryPhotos(data.photos || []);
        setValue("title", data.title);
        setValue("description", data.description);
        setValue("place", data.place);
        setValue("date", data.date?.split("T")[0]);
        setValue("time", data.date?.split("T")[1]?.slice(0, 5));
        setValue("endDate", data.endDate?.split("T")[0]);
        setValue("endTime", data.endDate?.split("T")[1]?.slice(0, 5));
        setValue("ticketCCEmail", data.ticketCCEmail);
        setValue("supportPhoneNumber", data.supportPhoneNumber);
        setValue("publicKey", data.publicKey);
        setValue("privateKey", data.privateKey);
        setValue("isActive", data.isActive);
        setValue("isPremium", data.isPremium);
        setValue("paymentModeIds", data.paymentModeIds);
        hasSeatSettingRef.current = data.hasSeatSelection !== undefined;
        setValue("hasSeatSelection", !!data.hasSeatSelection);
        setValue("venueId", data.venueId || "");
        // One option per line is the easiest list to edit by hand
        setValue("customFieldLabel", data.customField?.label || "");
        setValue("customFieldOptions", (data.customField?.options || []).join("\n"));
        setValue("customFieldRequired", !!data.customField?.isRequired);
        // Report settings: empty means "use the default"
        const billing = data.billing || {};
        setValue("salesCommission", billing.salesCommission ?? 8);
        setValue("cooperativeCommission", billing.cooperativeCommission ?? 5);
        setValue("invitationLimit", billing.invitationLimit ?? 50);
        setValue("invitationPrice", billing.invitationPrice ?? 0.5);
        setValue("accessControlPersonPrice", billing.accessControlPersonPrice ?? 20);
        setValue("accessControlPeopleCount", billing.accessControlPeopleCount ?? 2);
      }
    });
  }, [setValue, eventId]);

  useEffect(() => {
    getVenuesAdmin().then((res) => {
      setVenues(res?.data?.items || []);
    });
  }, []);

  useEffect(() => {
    getPaymentModes().then((res) => {
      const paymentModes = res?.data?.items || [];
      setPaymentModes(paymentModes);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePhotoUpload = async () => {
    if (!photoFile) return;
    setIsUploadingPhoto(true);
    try {
      const res = await uploadEventPhoto(eventId, photoFile);
      setCurrentPhoto(res?.data?.photo || null);
      setPhotoFile(null);
    } finally {
      setIsUploadingPhoto(false);
    }
  };

  const handleGalleryUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setIsUploadingGallery(true);
    try {
      const res = await uploadGalleryPhoto(eventId, file);
      setGalleryPhotos(res?.data?.photos || []);
    } finally {
      setIsUploadingGallery(false);
      e.target.value = '';
    }
  };

  const handleGalleryDelete = async (photoUrl) => {
    const res = await deleteGalleryPhoto(eventId, photoUrl);
    setGalleryPhotos(res?.data?.photos || []);
  };

  const onSubmit = async (formData) => {
    const payload = {
      title: formData.title,
      description: formData.description,
      place: formData.place,
      date: formData.date + "T" + formData?.time + ":00",
      endDate: formData.endDate + "T" + formData?.endTime + ":00",
      ticketCCEmail: formData.ticketCCEmail,
      supportPhoneNumber: formData.supportPhoneNumber,
      publicKey: formData.publicKey,
      privateKey: formData.privateKey,
      isActive: formData.isActive,
      isPremium: formData.isPremium,
      paymentModeIds: formData.paymentModeIds || [],
    };

    // The event's own question for buyers: empty label and options remove it
    const optionList = (formData.customFieldOptions || "")
      .split("\n")
      .map((option) => option.trim())
      .filter(Boolean);
    const label = (formData.customFieldLabel || "").trim();
    if (!label && optionList.length) {
      setError("customFieldLabel", { type: "manual", message: "Sualın adını yazın" });
      return;
    }
    if (label && !optionList.length) {
      setError("customFieldOptions", { type: "manual", message: "Ən azı bir variant yazın" });
      return;
    }
    payload.customField = label ? { label, options: optionList, isRequired: !!formData.customFieldRequired } : null;

    payload.billing = {
      salesCommission: Number(formData.salesCommission),
      cooperativeCommission: Number(formData.cooperativeCommission),
      invitationLimit: Number(formData.invitationLimit),
      invitationPrice: Number(formData.invitationPrice),
      accessControlPersonPrice: Number(formData.accessControlPersonPrice),
      accessControlPeopleCount: Number(formData.accessControlPeopleCount),
    };

    if (formData.hasSeatSelection) {
      payload.hasSeatSelection = true;
      payload.venueId = formData.venueId;
    } else if (hasSeatSettingRef.current) {
      payload.hasSeatSelection = false;
    }

    try {
      await updateEventByIdAdmin(eventId, payload);
    } catch (error) {
      setError("root.serverError", { type: "server", message: error?.response?.data?.message || "Yadda saxlamaq olmadı." });
      return;
    }

    navigate(`/dashboard/${eventId}`);
  };
  if (!isAdmin) return <Navigate to={`/dashboard/${eventId}`} replace />;


  return (
    <DashboardLayout id={eventId}>
      <div className="max-w-2xl mx-auto mt-10 bg-white p-8 rounded-xl shadow">
        <h2 className="text-2xl font-bold mb-6">
          ⚙️ Parametrlər
        </h2>

        {/* Photo upload section */}
        <div className="mb-6 p-4 border border-gray-200 rounded-lg">
          <h3 className="text-base font-semibold mb-3">Şəkil</h3>
          {currentPhoto && (
            <img src={currentPhoto} alt="Cari şəkil" className="w-full max-h-48 object-cover rounded mb-3" />
          )}
          <div className="flex flex-wrap items-center gap-3">
            <input
              type="file"
              accept="image/*"
              onChange={(e) => setPhotoFile(e.target.files[0] || null)}
              className="text-sm text-gray-600 max-w-full"
            />
            <button
              type="button"
              onClick={handlePhotoUpload}
              disabled={!photoFile || isUploadingPhoto}
              className="bg-gray-800 text-white px-3 py-1.5 rounded text-sm hover:bg-gray-700 disabled:bg-gray-400"
            >
              {isUploadingPhoto ? "Yüklənir..." : "Yüklə"}
            </button>
          </div>
        </div>

        {/* Gallery section */}
        <div className="mb-6 p-4 border border-gray-200 rounded-lg">
          <h3 className="text-base font-semibold mb-3">Qalereya</h3>
          {galleryPhotos.length > 0 && (
            <div className="grid grid-cols-3 gap-2 mb-3">
              {galleryPhotos.map((url, i) => (
                <div key={i} className="relative group">
                  <img src={url} alt={`Şəkil ${i + 1}`} className="w-full h-24 object-cover rounded" />
                  <button
                    type="button"
                    onClick={() => handleGalleryDelete(url)}
                    className="absolute top-1 right-1 bg-red-600 text-white rounded-full w-5 h-5 text-xs flex items-center justify-center opacity-0 group-hover:opacity-100 transition"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
          <label className={`inline-flex items-center gap-2 px-3 py-1.5 rounded text-sm cursor-pointer ${isUploadingGallery ? 'bg-gray-400' : 'bg-gray-800 hover:bg-gray-700'} text-white`}>
            {isUploadingGallery ? "Yüklənir..." : "+ Şəkil əlavə et"}
            <input
              type="file"
              accept="image/*"
              onChange={handleGalleryUpload}
              disabled={isUploadingGallery}
              className="hidden"
            />
          </label>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700">Ad</label>
            <input
              {...register("title", { required: true })}
              className="mt-1 w-full border border-gray-300 p-2 rounded"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Açıqlama</label>

            <Controller
              name="description"
              control={control}
              defaultValue=""
              rules={{ required: "Description is required" }}
              render={({ field }) => (
                <Editor
                  value={field.value}
                  onTextChange={(e) => field.onChange(e.htmlValue)}
                  style={{ height: '320px' }} 
                />
                )}
              />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Dəstək üçün Telefon</label>
            <input
              {...register("supportPhoneNumber", { required: true })}
              className="mt-1 w-full border border-gray-300 p-2 rounded"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Məkan</label>
            <input
              {...register("place", { required: true })}
              className="mt-1 w-full border border-gray-300 p-2 rounded"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Tarix</label>
            <div className="flex space-x-2">
              <input
                type="date"
                {...register("date", { required: true })}
                className="mt-1 flex-[2] border border-gray-300 p-2 rounded"
              />
              <input
                type="time"
                {...register("time", { required: true })}
                className="mt-1 flex-[1] border border-gray-300 p-2 rounded"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Son Tarix</label>
            <div className="flex space-x-2">
              <input
                type="date"
                {...register("endDate", { required: false })}
                className="mt-1 flex-[2] border border-gray-300 p-2 rounded"
              />
              <input
                type="time"
                {...register("endTime", { required: false })}
                className="mt-1 flex-[1] border border-gray-300 p-2 rounded"
              />
            </div>
          </div>

          <h3 className="text-lg font-semibold mt-6">Ödəmə növləri</h3>
              
          {paymentModes?.map(paymentMode => <div className="flex items-center">
            <input
              type="checkbox"
              value={paymentMode._id}
              {...register("paymentModeIds[]")}
              className="mr-2"
            />
            <label className="text-sm text-gray-700">{paymentMode?.name?.az}</label>
          </div>)}


          <h3 className="text-lg font-semibold mt-6">Yer seçimi</h3>

          <div className="flex items-center">
            <input
              id="hasSeatSelection"
              type="checkbox"
              {...register("hasSeatSelection")}
              className="mr-2"
            />
            <label htmlFor="hasSeatSelection" className="text-sm text-gray-700">
              Alıcılar zal sxemindən yer seçsin
            </label>
          </div>

          {hasSeatSelection && (
            <div>
              <label className="block text-sm font-medium text-gray-700">Zal sxemi</label>
              <Controller
                name="venueId"
                control={control}
                rules={{ validate: (value, values) => !values.hasSeatSelection || !!value || "Zal sxemi seçin" }}
                render={({ field }) => (
                  <select
                    {...field}
                    value={field.value || ""}
                    className="mt-1 w-full border border-gray-300 p-2 rounded bg-white"
                  >
                    <option value="">Seçin...</option>
                    {venues.map((venue) => (
                      <option key={venue._id} value={venue._id}>
                        {venue.name}
                      </option>
                    ))}
                  </select>
                )}
              />
              {errors?.venueId && <p className="text-sm text-red-600 mt-1">{errors.venueId.message}</p>}
              <p className="text-xs text-gray-500 mt-1">
                Hansı yerin hansı bilet növü ilə satıldığını "Yer planı" bölməsində təyin edin.
              </p>
            </div>
          )}

          <h3 className="text-lg font-semibold mt-6">Hesabat parametrləri</h3>
          <p className="text-xs text-gray-500 -mt-4">
            "Hesabatlar" bölməsindəki fakturada istifadə olunur.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Satılan biletlərdən komissiya (%)</label>
              <input type="number" step="0.01" min="0" max="100" {...register("salesCommission", { required: true })}
                className="mt-1 w-full border border-gray-300 p-2 rounded" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Korporativ biletlərdən komissiya (%)</label>
              <input type="number" step="0.01" min="0" max="100" {...register("cooperativeCommission", { required: true })}
                className="mt-1 w-full border border-gray-300 p-2 rounded" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Dəvətnamə limiti</label>
              <input type="number" step="1" min="0" {...register("invitationLimit", { required: true })}
                className="mt-1 w-full border border-gray-300 p-2 rounded" />
              <p className="text-xs text-gray-500 mt-1">Limit dolduqda yeni dəvətnamə yaratmaq olmur.</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Dəvətnamə qiyməti (₼)</label>
              <input type="number" step="0.01" min="0" {...register("invitationPrice", { required: true })}
                className="mt-1 w-full border border-gray-300 p-2 rounded" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Access control — 1 nəfər (₼)</label>
              <input type="number" step="0.01" min="0" {...register("accessControlPersonPrice", { required: true })}
                className="mt-1 w-full border border-gray-300 p-2 rounded" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Access control — nəfər sayı</label>
              <input type="number" step="1" min="0" {...register("accessControlPeopleCount", { required: true })}
                className="mt-1 w-full border border-gray-300 p-2 rounded" />
            </div>
          </div>

          <h3 className="text-lg font-semibold mt-6">Əlavə sual</h3>
          <p className="text-xs text-gray-500 -mt-4">
            Bilet alan şəxs bu sualın variantlarından birini seçir. Cavab yalnız məlumat üçündür, qiymətə təsir etmir.
            Sualı silmək üçün adı və variantları boş saxlayın.
          </p>

          <div>
            <label className="block text-sm font-medium text-gray-700">Sualın adı</label>
            <input
              {...register("customFieldLabel")}
              placeholder="Məsələn: Hansı şəhərdən gəlirsiniz?"
              className="mt-1 w-full border border-gray-300 p-2 rounded"
            />
            {errors?.customFieldLabel && <p className="text-sm text-red-600 mt-1">{errors.customFieldLabel.message}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Variantlar <span className="text-gray-400">(hər sətirdə bir variant)</span>
            </label>
            <textarea
              {...register("customFieldOptions")}
              rows={6}
              placeholder={"Bakı\nGəncə\nSumqayıt"}
              className="mt-1 w-full border border-gray-300 p-2 rounded font-mono text-sm"
            />
            {errors?.customFieldOptions && <p className="text-sm text-red-600 mt-1">{errors.customFieldOptions.message}</p>}
            <p className="text-xs text-gray-500 mt-1">
              {customFieldLabel || customFieldOptions
                ? `${(customFieldOptions || "").split("\n").map((option) => option.trim()).filter(Boolean).length} variant`
                : "Boşdursa, bilet alma səhifəsində heç nə görünmür."}
            </p>
          </div>

          <div className="flex items-center">
            <input
              id="customFieldRequired"
              type="checkbox"
              {...register("customFieldRequired")}
              className="mr-2"
            />
            <label htmlFor="customFieldRequired" className="text-sm text-gray-700">
              Seçim mütləqdir
            </label>
          </div>

          <h3 className="text-lg font-semibold mt-6">API Parametrləri</h3>

          <div>
            <label className="block text-sm font-medium text-gray-700">Email (bilet kopyası üçün)</label>
            <input
              type="email"
              {...register("ticketCCEmail", { required: false })}
              className="mt-1 w-full border border-gray-300 p-2 rounded"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Public Key</label>
            <input
              {...register("publicKey", { required: false })}
              className="mt-1 w-full border border-gray-300 p-2 rounded"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Private Key</label>
            <input
              {...register("privateKey", { required: false })}
              className="mt-1 w-full border border-gray-300 p-2 rounded"
            />
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              {...register("isActive")}
              className="mr-2"
            />
            <label className="text-sm text-gray-700">Aktivdir</label>
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              {...register("isPremium")}
              className="mr-2"
            />
            <label className="text-sm text-gray-700">Premium (əsas səhifədə göstər)</label>
          </div>

          {errors?.root?.serverError && (
            <p className="text-sm text-red-600">{errors.root.serverError.message}</p>
          )}

          <button
            type="submit"
            disabled={isSubmitting}
            className="bg-black text-white px-4 py-2 rounded hover:bg-gray-800"
          >
            Yadda saxla
          </button>
        </form>
      </div>
    </DashboardLayout>
  );
};

export default SettingsPage;
